import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Crown, Moon, Calendar, MapPin, ArrowRight, FileText, Users, CreditCard, Sparkles, Download, RefreshCw, BarChart3, Clock, Plus, Shield, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getUserProfile, generateNatalReport, generateCompatibilityReport, generateDashaReport, getReportUsage } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Profile() {
  const { user, loading: authLoading, loginWithGoogle } = useAuth();
  const [profile, setProfile] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    
    // Fetch profile and usage data in parallel
    Promise.all([
      getUserProfile().catch(() => null),
      getReportUsage().catch(() => null)
    ])
      .then(([profileData, usageData]) => {
        setProfile(profileData);
        setUsage(usageData);
      })
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <img src="/logo.png" alt="Daily Nakshatra" className="h-12 w-auto logo-pulse" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center px-6">
          <img src="/logo.png" alt="Daily Nakshatra" className="h-12 w-auto mx-auto mb-4 opacity-40" />
          <h1 className="text-3xl font-light text-white/90 mb-3">Sign in to view profile</h1>
          <p className="text-white/40 text-sm mb-6">Your natal data and purchase history in one place.</p>
          <Button onClick={loginWithGoogle} className="bg-white text-black hover:bg-white/90 rounded-full px-6 h-11 text-sm" data-testid="profile-login-btn">
            Sign in with Google
          </Button>
          <div className="mt-4">
            <Link to="/" className="text-white/30 text-sm hover:text-white/50">Back to Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  const u = profile?.user || user;
  const natal = profile?.natal;
  const purchases = profile?.purchases || [];

  const packageIcons = { 
    premium_monthly: Crown, 
    natal_report: FileText, 
    compatibility: Users, 
    compatibility_report: FileText,
    dasha_forecast: Sparkles 
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors" data-testid="profile-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase">Dashboard</span>
          </Link>
        </div>
      </nav>

      <main className="pt-14 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {/* User Header */}
          <div className="flex items-center gap-5 mb-10" data-testid="profile-header">
            {u.picture ? (
              <img src={u.picture} alt="" className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                <Moon className="w-7 h-7 text-white/30" />
              </div>
            )}
            <div>
              <h1 className="text-2xl sm:text-3xl font-light text-white/95" data-testid="profile-name">{u.name}</h1>
              <p className="text-white/40 text-sm">{u.email}</p>
              {u.is_premium ? (
                <div className="flex items-center gap-1.5 mt-1">
                  <Crown className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-amber-400 text-xs tracking-wider uppercase">Premium Member</span>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  className="text-white/40 hover:text-amber-400 text-xs mt-1 p-0 h-auto"
                  onClick={() => navigate("/pricing")}
                  data-testid="profile-upgrade-btn"
                >
                  <Crown className="w-3 h-3 mr-1" /> Upgrade to Premium
                </Button>
              )}
            </div>
          </div>

          {/* Usage Dashboard - Premium Users Only */}
          {u.is_premium && usage?.is_premium && (
            <section className="mb-8" data-testid="profile-usage-section">
              <h2 className="text-xl font-light text-white/80 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-400/70" />
                Report Usage
              </h2>
              <div
                className="rounded-lg p-6"
                style={{ background: "rgba(251,191,36,0.03)", border: "1px solid rgba(251,191,36,0.15)" }}
              >
                {/* Usage Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 rounded-lg bg-white/[0.02]">
                    <p className="text-2xl font-light text-white/90" data-testid="usage-used">{usage.used || 0}</p>
                    <p className="text-white/40 text-xs tracking-wider uppercase mt-1">Used</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-white/[0.02]">
                    <p className="text-2xl font-light text-amber-400" data-testid="usage-remaining">{usage.remaining_included || 0}</p>
                    <p className="text-white/40 text-xs tracking-wider uppercase mt-1">Included</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-white/[0.02]">
                    <p className="text-2xl font-light text-emerald-400" data-testid="usage-credits">{usage.additional_credits || 0}</p>
                    <p className="text-white/40 text-xs tracking-wider uppercase mt-1">Purchased</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-white/[0.02]">
                    <p className="text-2xl font-light text-white/90" data-testid="usage-total">{usage.total_remaining || 0}</p>
                    <p className="text-white/40 text-xs tracking-wider uppercase mt-1">Available</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-white/40 mb-2">
                    <span>Monthly Usage</span>
                    <span>{usage.used || 0} / {usage.limit || 4} included reports</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div 
                      className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500"
                      style={{ width: `${Math.min(100, ((usage.used || 0) / (usage.limit || 4)) * 100)}%` }}
                    />
                  </div>
                </div>

                {/* Reset Info & Actions */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-white/5">
                  <div className="flex items-center gap-2 text-white/40 text-xs">
                    <Clock className="w-3.5 h-3.5" />
                    <span>
                      {usage.reset_date 
                        ? `Resets on ${usage.reset_date}` 
                        : `Resets on the 1st of next month (UTC)`}
                    </span>
                  </div>
                  {(usage.remaining_included === 0 || usage.total_remaining === 0) && (
                    <Button
                      onClick={() => navigate("/calculator")}
                      size="sm"
                      className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 rounded-full text-xs h-8"
                      data-testid="usage-buy-more-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Purchase Additional — $1.00
                    </Button>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Non-Premium Upgrade CTA */}
          {!u.is_premium && (
            <section className="mb-8" data-testid="profile-upgrade-section">
              <div
                className="rounded-lg p-6"
                style={{ background: "linear-gradient(135deg, rgba(251,191,36,0.08) 0%, rgba(251,191,36,0.02) 100%)", border: "1px solid rgba(251,191,36,0.2)" }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Crown className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white/90 font-medium mb-1">Unlock Premium Reports</h3>
                    <p className="text-white/50 text-sm mb-3">
                      Get 4 detailed AI-generated reports per month — Natal, Compatibility, and Dasha Forecast. Additional reports just $1.00 each.
                    </p>
                    <Button
                      onClick={() => navigate("/pricing")}
                      className="bg-amber-500 text-black hover:bg-amber-400 rounded-full px-5 h-9 text-sm font-medium"
                      data-testid="profile-upgrade-cta-btn"
                    >
                      Subscribe — $4.29/mo
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Natal Placement */}
          <section className="mb-8" data-testid="profile-natal-section">
            <h2 className="text-xl font-light text-white/80 mb-4">Natal Moon Placement</h2>
            {natal ? (
              <div
                className="rounded-lg p-6"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-white/30 text-xs tracking-[0.15em] uppercase mb-2">Your Moon</p>
                    <h3 className="text-2xl font-light text-white/95 mb-1" data-testid="profile-natal-name">
                      {natal.nakshatra_name}
                    </h3>
                    <p className="text-white/60 text-base mb-3">Pada {natal.pada}</p>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                      <span className="text-white/40">
                        <span className="text-white/60">{natal.degree_in_sign}&deg; {natal.sidereal_sign}</span>
                      </span>
                      <span className="text-white/40">
                        Ruler: <span className="text-white/60">{natal.ruler}</span>
                      </span>
                    </div>
                    {natal.birth_city && (
                      <div className="flex items-center gap-1.5 mt-3 text-white/30 text-xs">
                        <MapPin className="w-3 h-3" />
                        <span>{natal.birth_city}</span>
                        {natal.birth_date && <><Calendar className="w-3 h-3 ml-2" /><span>{natal.birth_date}</span></>}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    className="text-white/40 hover:text-white text-xs"
                    onClick={() => navigate(`/nakshatras/${natal.nakshatra_slug}`)}
                  >
                    Explore <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="rounded-lg p-6 text-center"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
              >
                <p className="text-white/30 text-sm mb-3">No natal data saved yet.</p>
                <Button
                  onClick={() => navigate("/calculator")}
                  className="bg-white/10 text-white/70 hover:bg-white/15 border border-white/10 rounded-full px-5 h-9 text-xs"
                  data-testid="profile-calc-btn"
                >
                  Calculate Your Nakshatra
                </Button>
              </div>
            )}
          </section>

          {/* Purchase History */}
          <section data-testid="profile-purchases-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-light text-white/80">Purchase History</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/my-reports")}
                className="text-white/40 hover:text-white text-xs h-auto p-0"
                data-testid="profile-my-reports-link"
              >
                <FileText className="w-3.5 h-3.5 mr-1" /> My Reports
              </Button>
            </div>
            {purchases.length > 0 ? (
              <div className="space-y-3">
                {purchases.map((tx, i) => {
                  const Icon = packageIcons[tx.package_id] || CreditCard;
                  const canRegenerate = ["natal_report", "compatibility_report", "dasha_forecast"].includes(tx.package_id);
                  
                  return (
                    <PurchaseItem 
                      key={tx.id || i} 
                      tx={tx} 
                      index={i} 
                      Icon={Icon} 
                      canRegenerate={canRegenerate}
                      natal={natal}
                    />
                  );
                })}
              </div>
            ) : (
              <div
                className="rounded-lg p-6 text-center"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
              >
                <p className="text-white/30 text-sm mb-3">No purchases yet.</p>
                <Button
                  onClick={() => navigate("/pricing")}
                  className="bg-white/10 text-white/70 hover:bg-white/15 border border-white/10 rounded-full px-5 h-9 text-xs"
                >
                  View Premium Offerings
                </Button>
              </div>
            )}
          </section>
        </motion.div>

        {/* Account & Privacy */}
        <motion.section
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-10 pt-8 border-t border-white/5"
          data-testid="profile-account-settings"
        >
          <h2 className="text-xl font-light text-white/80 mb-4">Account &amp; Privacy</h2>
          <div className="space-y-2">
            <Link
              to="/privacy"
              className="flex items-center justify-between rounded-lg p-4 hover:bg-white/[0.03] transition-colors"
              style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
              data-testid="profile-privacy-link"
            >
              <span className="flex items-center gap-3 text-white/70 text-sm"><Shield className="w-4 h-4 text-white/40" /> Privacy Policy</span>
              <ArrowRight className="w-4 h-4 text-white/30" />
            </Link>
            <Link
              to="/delete-account"
              className="flex items-center justify-between rounded-lg p-4 hover:bg-red-500/[0.06] transition-colors"
              style={{ background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.15)" }}
              data-testid="profile-delete-account-link"
            >
              <span className="flex items-center gap-3 text-red-300/80 text-sm"><Trash2 className="w-4 h-4 text-red-400/70" /> Delete Account</span>
              <ArrowRight className="w-4 h-4 text-red-400/40" />
            </Link>
          </div>
        </motion.section>
      </main>
    </div>
  );
}

// Component for each purchase item with regenerate capability
function PurchaseItem({ tx, index, Icon, canRegenerate, natal }) {
  const [regenerating, setRegenerating] = useState(false);
  const navigate = useNavigate();

  const handleRegenerate = async () => {
    // Get metadata from the transaction
    const meta = tx.metadata || {};
    
    setRegenerating(true);
    try {
      let blob;
      let filename;

      if (tx.package_id === "natal_report") {
        // Use saved natal data or metadata
        const reportData = {
          name: meta.name || natal?.name || "Seeker",
          birth_date: meta.birth_date || natal?.birth_date,
          birth_time: meta.birth_time || natal?.birth_time,
          birth_city: meta.birth_city || natal?.birth_city,
        };
        
        if (!reportData.birth_date || !reportData.birth_time || !reportData.birth_city) {
          toast.error("Birth data not found. Please recalculate your natal chart first.");
          navigate("/calculator");
          return;
        }
        
        blob = await generateNatalReport(reportData);
        filename = "natal_report.pdf";
        
      } else if (tx.package_id === "compatibility_report") {
        // Compatibility report needs both people's data from metadata
        const reportData = {
          person1_name: meta.person1_name,
          person1_birth_date: meta.person1_birth_date,
          person1_birth_time: meta.person1_birth_time,
          person1_birth_city: meta.person1_birth_city,
          person2_name: meta.person2_name,
          person2_birth_date: meta.person2_birth_date,
          person2_birth_time: meta.person2_birth_time,
          person2_birth_city: meta.person2_birth_city,
        };
        
        if (!reportData.person1_birth_date || !reportData.person2_birth_date) {
          toast.error("Compatibility data not found in purchase history.");
          return;
        }
        
        blob = await generateCompatibilityReport(reportData);
        filename = "compatibility_report.pdf";
        
      } else if (tx.package_id === "dasha_forecast") {
        // Dasha report
        const reportData = {
          name: meta.name || natal?.name || "Seeker",
          birth_date: meta.birth_date || natal?.birth_date,
          birth_time: meta.birth_time || natal?.birth_time,
          birth_city: meta.birth_city || natal?.birth_city,
        };
        
        if (!reportData.birth_date || !reportData.birth_time || !reportData.birth_city) {
          toast.error("Birth data not found. Please save your natal data first.");
          navigate("/calculator");
          return;
        }
        
        blob = await generateDashaReport(reportData);
        filename = "dasha_forecast.pdf";
      }

      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Report downloaded!");
      }
    } catch (e) {
      console.error("Regenerate error:", e);
      toast.error("Failed to regenerate report. Please try again.");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div
      className="rounded-lg p-4"
      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
      data-testid={`purchase-${index}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center">
            <Icon className="w-4 h-4 text-white/40" />
          </div>
          <div>
            <p className="text-white/80 text-sm">{tx.package_name}</p>
            <p className="text-white/30 text-xs">
              {tx.created_at ? new Date(tx.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {canRegenerate && tx.payment_status === "paid" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRegenerate}
              disabled={regenerating}
              className="text-white/40 hover:text-white hover:bg-white/5 text-xs"
              data-testid={`regenerate-${index}`}
            >
              {regenerating ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Download PDF
                </>
              )}
            </Button>
          )}
          <div className="text-right">
            <p className="text-white/70 text-sm">${tx.amount?.toFixed(2)}</p>
            <p className={`text-xs ${tx.payment_status === "paid" ? "text-emerald-400" : "text-white/30"}`}>
              {tx.payment_status}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
